import { useState, useRef, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Users, Clock3, CircleHelp, CalendarCheck, CalendarX2, Info, MessageCircleQuestion, UserPlus, ListOrdered, Repeat, CalendarClock, Hourglass } from "lucide-react";
import { toast } from "sonner";

import { text, participationState, accessFeatures, accessDirections, spotsLeft } from "@/lib/memberDerive";
import { activityWhen, activityPlace } from "@/lib/v3Views";
import { participationActions, participationOutcome, ACTION_FAIL, actionPath, OFFER_ACCEPT_PATH, offerPostKind, offerNeedsRead, offerAcceptOutcome } from "@/lib/activityParticipation";
import { ACCESS_PILL, accessMeta, seatsText, companionSeats, companionChoices, companionLabel, hasActiveCompanions, ownCompanionText, changeInfo, seriesId, activityDay, OFFER_TITLE, OFFER_TEXT } from "@/lib/v3Activities";
import { useMemberAuth } from "../MemberAuthContext";
import { useCore } from "../useCore";
import { requiredCollection } from "../requiredCollection";
import { runAction, loadActivity, acceptOffer } from "../actions";
import { shortName } from "../navSections";
import { SaveToggle } from "../components/SaveToggle";
import { ConnectionError } from "../components/ConnectionError";
import { Loading, Unavailable, ConfirmDialog, ErrorNote } from "../components/kit";
import { V3Title, V3Columns, V3Actions, StatusBanner, DetailRow, DetailList, AsideCard, V3_PRIMARY, V3_SECONDARY } from "../components/v3";
import { AreaCard, ResponsibleCard, SectionTitle, Pill, V3Select, V3_GHOST } from "../components/v3app";
import { ProfileHead, InfoBox, StagePill } from "../components/v3people";
import { ActivityHero, activityHref } from "../components/v3act";

// Core 1.1 has no detail endpoint: the activity comes from the list; a fresh read after an action wins.
function ActivityLoader({ id, children }) {
  const res = useCore("/member/activities?category=all", (d) => requiredCollection(d, "activities"));
  const [fresh, setFresh] = useState(null);
  if (res.error) return <ConnectionError onRetry={res.reload} />;
  if (res.data === null) return <Loading />;
  const listed = res.data.find((x) => x && String(x.id) === id) || null;
  const a = fresh && String(fresh.id) === id ? fresh : listed;
  if (!a) return (<><V3Title title="Aktivität nicht verfügbar" testid="activity-detail-title" />
    <Unavailable testid="activity-not-found">Diese Aktivität ist nicht (mehr) verfügbar. Sie wurde vielleicht beendet oder ist für dich nicht sichtbar.</Unavailable>
    <Link to="/aktivitaeten" className={`${V3_SECONDARY} mt-[18px]`} data-testid="activity-back">Zu den Aktivitäten</Link></>);
  return children(a, setFresh);
}

// Keyed by account and id so nothing of a previous account or activity stays visible.
export function WithActivity({ children }) {
  const { id } = useParams();
  const { member } = useMemberAuth();
  return <ActivityLoader key={`${member?.id || "anon"}:${id}`} id={id}>{children}</ActivityLoader>;
}

// Guards against double submission and state updates after unmount.
function useGuard() {
  const alive = useRef(true);
  const flight = useRef(false);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  return { alive, flight };
}

// M62–M66, M104, M106, M107: POST (join / leave v2.6, offer/accept v2.10), then only a fresh Core read decides what is shown; the re-check is GET-only.
export function useParticipation(a, onFresh) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [outcome, setOutcome] = useState(null);
  const { alive, flight } = useGuard();
  const state = outcome?.result === "unknown" ? "unknown" : participationState(a);
  const can = participationActions(state, { blocked: state === "unknown" });
  const settle = (o, fresh) => {
    setOutcome(o);
    if (fresh) onFresh(fresh);
    if (o.result === "success") toast.success(o.message);
  };
  const act = async (kind, body) => {
    if (flight.current) return false;
    flight.current = true; setBusy(true); setError("");
    const postError = await runAction("post", `/member/activities/${encodeURIComponent(a.id)}/${actionPath(kind)}`, ACTION_FAIL[kind], body);
    const read = postError ? null : await loadActivity(a.id);
    flight.current = false;
    if (!alive.current) return false;
    setBusy(false);
    const o = participationOutcome(kind, postError ? { postError } : read);
    if (o.result === "failed") { setError(o.message); return false; }
    settle(o, read?.ok ? read.activity : null);
    return true;
  };
  // 409 (capacity_conflict, offer_not_open, activity_closed), 404, lost connection or 200: always a fresh GET before anything is shown.
  const accept = async () => {
    if (flight.current) return false;
    flight.current = true; setBusy(true); setError("");
    const post = offerPostKind(await acceptOffer(OFFER_ACCEPT_PATH(a.id)));
    const read = offerNeedsRead(post) ? await loadActivity(a.id) : null;
    flight.current = false;
    if (!alive.current) return false;
    setBusy(false);
    const o = offerAcceptOutcome(post, read);
    if (o.result === "failed") { setError(o.message); return false; }
    settle(o, read?.ok ? read.activity : null);
    return o.result === "success";
  };
  const recheck = async () => {
    if (flight.current) return;
    flight.current = true; setBusy(true);
    const read = await loadActivity(a.id);
    flight.current = false;
    if (!alive.current) return;
    setBusy(false);
    settle(outcome?.kind === "accept" ? offerAcceptOutcome("recheck", read) : participationOutcome(outcome?.kind || "join", read), read.ok ? read.activity : null);
  };
  return { state, can, busy, error, setError, outcome, act, accept, recheck };
}

const stand = (f) => (f.updatedAt ? new Date(f.updatedAt).toLocaleDateString("de-CH") : "–");

function AccessList({ activity }) {
  const directions = accessDirections(activity);
  const meta = accessMeta(activity);
  return (
    <section aria-labelledby="activity-access-h" className="mt-[18px]" data-testid="activity-access">
      <SectionTitle id="activity-access-h">Zugangsangaben</SectionTitle>
      <ul className="mt-[12px] space-y-[8px]">
        {accessFeatures(activity).filter((f) => f.key !== "assistance_dog_info").map((f) => (
          <li key={f.key} className="rounded-[8px] border border-v3-line bg-white px-[14px] py-[9px]" data-testid={`activity-access-${f.key}`} data-state={f.state}>
            <span className="flex min-h-[32px] items-center justify-between gap-3"><span className="text-[14px] font-bold text-v3-ink">{f.label}</span><Pill tone={ACCESS_PILL[f.state][0]}>{ACCESS_PILL[f.state][1]}</Pill></span>
            {f.state !== "unknown" && <span className="block text-[12.5px] text-v3-muted">Quelle: {f.source || "keine Angabe"} · Stand: {f.updatedAt ? new Date(f.updatedAt).toLocaleDateString("de-CH") : "–"}</span>}
          </li>
        ))}
      </ul>
      <p className="mt-[10px] text-[12.5px] text-v3-muted" data-testid="activity-access-meta">{meta.source ? `Quelle: ${meta.source}` : "Quelle: keine Angabe"} · Stand {meta.updatedAt ? new Date(meta.updatedAt).toLocaleDateString("de-CH") : "–"} · keine Zertifizierung</p>
      {directions && <div className="mt-[10px] rounded-[8px] bg-v3-lilac px-[14px] py-[10px] text-[13.5px] text-v3-ink" data-testid="activity-access-directions">
        <p className="font-bold">Hinweise zum Weg (Freitext, kein bestätigtes Merkmal)</p>
        <p className="mt-1 whitespace-pre-line break-words">{directions}</p>
      </div>}
    </section>
  );
}

const PRIMARY = { none: "Teilnahme anfragen", cancelled: "Erneut anfragen", requested: "Anfrage ansehen", waitlisted: "Warteliste ansehen", offered: "Angebot ansehen", confirmed: "Teilnahme ansehen", registered: "Teilnahme ansehen", activity_cancelled: "Absage ansehen", declined: "Status ansehen", unknown: "Status prüfen" };
const ASK_TEXT = { none: "Bis zu einer ausdrücklichen Annahme ist die Teilnahme noch nicht bestätigt.", cancelled: "Du hast abgesagt. Du kannst dich erneut anmelden.", requested: "Deine Anfrage ist offen – noch nicht bestätigt.", waitlisted: "Du stehst auf der Warteliste – noch nicht zugesagt.", offered: OFFER_TEXT, confirmed: "Deine Teilnahme ist bestätigt.", registered: "Du bist eingetragen.", activity_cancelled: "Die Aktivität wurde abgesagt.", declined: "Deine Anfrage wurde abgelehnt.", unknown: "Dein Status ist noch unbekannt." };
const FULL_TEXT = "Die Aktivität ist voll. Eine Anfrage setzt dich auf die Warteliste; frei werdende Plätze werden nicht automatisch vergeben.";
const isFresh = (state) => state === "none" || state === "cancelled";

function DetailLinks({ a, state }) {
  const full = spotsLeft(a) === 0;
  const links = [
    ["/zugang", "Zugangsangaben im Detail", Info, "activity-access-detail-link"],
    ["/frage", "Frage zum Zugang stellen", MessageCircleQuestion, "activity-question"],
    ["/begleitung", hasActiveCompanions(a) && !["cancelled", "declined"].includes(state) ? "Begleitplatz ansehen" : "Begleitplatz anfragen", UserPlus, "activity-companion-link"],
    ...((full && isFresh(state)) || state === "waitlisted" || state === "offered" ? [["/warteliste", "Warteliste", ListOrdered, "activity-waitlist-link"]] : []),
    ...(changeInfo(a) ? [["/aenderung", "Terminänderung prüfen", CalendarClock, "activity-change-link"]] : []),
  ];
  return (
    <div className="mt-[16px] flex flex-wrap gap-2">
      {links.map(([to, l, Icon, t]) => <Link key={to} to={activityHref(a, to)} data-testid={t} className={V3_GHOST}><Icon className="h-4 w-4" aria-hidden="true" />{l}</Link>)}
      {seriesId(a) && <Link to={`/aktivitaeten/serie/${encodeURIComponent(seriesId(a))}`} data-testid="activity-series-link" className={V3_GHOST}><Repeat className="h-4 w-4" aria-hidden="true" />Weitere Termine der Serie</Link>}
    </div>
  );
}

function Detail({ a }) {
  const state = participationState(a);
  const seats = companionSeats(a);
  const mine = ownCompanionText(a);
  const fullFresh = spotsLeft(a) === 0 && isFresh(state);
  return (
    <div data-m="M12" data-testid="activity-detail">
      <V3Title title="Aktivitätsdetail und Teilnahme" intro="Prüfe Termin, Treffpunkt und Zugangsangaben, bevor du eine Teilnahme anfragst." testid="activity-detail-title" />
      <V3Columns layout="app" aside={<>
        <AsideCard icon={Users} title="Plätze und Begleitung" text={`${Number(a.capacity) > 0 ? `${a.capacity} Teilnehmendenplätze` : "Keine Platzbegrenzung angegeben"}. Begleitplätze: ${seats || "keine Angabe"}.`} testid="activity-seats-aside" />
        <AsideCard icon={Clock3} tone="white" title="Deine Anfrage" text={fullFresh ? FULL_TEXT : ASK_TEXT[state] || ASK_TEXT.unknown} testid="activity-ask-aside" />
      </>} main={<>
        <ActivityHero a={a} />
        <DetailList>
          <DetailRow label="Termin" value={activityWhen(a)} testid="activity-when" />
          <DetailRow label="Öffentlicher Treffpunkt" value={activityPlace(a)} testid="activity-place" />
          <DetailRow label="Teilnehmendenplätze" value={seatsText(a)} testid="activity-seats" />
          <DetailRow label="Begleitplätze" value={seats || "Keine Angabe"} testid="activity-companion-seats" />
          {mine && <DetailRow label="Deine Begleitung" value={mine} testid="activity-own-companions" />}
        </DetailList>
        <AccessList activity={a} />
        <V3Actions className="mt-[22px]">
          <Link to={activityHref(a, "/teilnahme")} data-testid={isFresh(state) ? "activity-join" : "activity-participation-open"} className={V3_PRIMARY}>{fullFresh ? "Auf die Warteliste" : PRIMARY[state] || PRIMARY.unknown}</Link>
          <Link to="/aktivitaeten" data-testid="activity-back" className={V3_SECONDARY}>Zurück</Link>
        </V3Actions>
        {fullFresh && <p className="mt-[12px] rounded-[10px] bg-v3-sand px-[14px] py-[10px] text-[14px] text-v3-amber lg:hidden" data-testid="activity-full-hint">{FULL_TEXT}</p>}
        {state === "offered" && <p className="mt-[12px] rounded-[10px] bg-v3-sand px-[14px] py-[10px] text-[14px] text-v3-amber lg:hidden" data-testid="activity-offer-hint"><strong>{OFFER_TITLE}.</strong> {OFFER_TEXT}</p>}
        <DetailLinks a={a} state={state} />
        <div className="mt-[12px]"><SaveToggle activityId={a.id} /></div>
      </>} />
    </div>
  );
}

// M12: detail as in the template; actions lead to the participation views (M62–M66).
export default function ActivityDetail() {
  return <WithActivity>{(a) => <Detail a={a} />}</WithActivity>;
}

const Aside = ({ a, title = "Dein Bereich" }) => <><AreaCard title={title} text={text(a.title) || "Aktivität"} testid="activity-flow-area" /><ResponsibleCard /></>;

export function Outcome({ p }) {
  const note = p.outcome && p.outcome.result !== "success" && p.outcome.message;
  return (<>
    {note && <p role="status" className="mt-[12px] rounded-[10px] bg-v3-sand px-[14px] py-[10px] text-[14px] text-v3-amber" data-testid="activity-outcome-note">{note}</p>}
    {p.error && <div className="mt-[12px]"><ErrorNote testid="activity-action-error">{p.error}</ErrorNote></div>}
  </>);
}

export function Recheck({ p }) {
  return <button type="button" onClick={p.recheck} disabled={p.busy} data-testid="activity-recheck" className={V3_GHOST}>Status erneut prüfen</button>;
}

// Optional companions (v2.6 POST …/join {companions: 1–3}); only offered when the activity has companion seats.
function CompanionPick({ a, value, onChange }) {
  const choices = companionChoices(a);
  if (!choices.length) return null;
  return (
    <div className="mt-[18px]">
      <V3Select id="activity-review-companions" label="Begleitpersonen (freiwillig)" value={String(value)} onChange={(e) => onChange(Number(e.target.value))} testid="activity-review-companions" hint="Begleitplätze werden getrennt bestätigt.">
        <option value="0">Ohne Begleitung</option>
        {choices.map((n) => <option key={n} value={n}>{companionLabel(n)}</option>)}
      </V3Select>
    </div>
  );
}

function Review({ a, p }) {
  const { member } = useMemberAuth();
  const [companions, setCompanions] = useState(0);
  const confirm = a.requires_confirmation === true;
  const full = spotsLeft(a) === 0;
  const label = full ? "Auf die Warteliste setzen" : confirm ? "Teilnahme anfragen" : "Teilnahme eintragen";
  return (
    <div data-m="M62" data-testid="activity-review">
      <V3Title title="Teilnahmeanfrage prüfen" intro={full ? "Die Aktivität ist voll. Mit deiner Anfrage kommst du auf die Warteliste." : confirm ? "Eine Anfrage ist noch keine bestätigte Teilnahme." : "Ob du eingetragen bist, zeigt die App erst nach der Bestätigung durch das VISIBILIS-System."} testid="activity-flow-title" />
      <V3Columns layout="app" aside={<Aside a={a} title="Ausgewählter Vorgang" />} main={<>
        <ProfileHead name={shortName(member?.name) || "Du"} place="" pill="Anfrage noch nicht gesendet" color={member?.avatar_color} testid="activity-review-head" />
        <StagePill testid="activity-review-stage">Vor der Entscheidung</StagePill>
        <DetailList>
          <DetailRow label="Aktivität" value={`${text(a.title)} · ${activityDay(a)}`} testid="activity-review-title" />
          <DetailRow label="Öffentlicher Treffpunkt" value={activityPlace(a)} testid="activity-review-place" />
          <DetailRow label="Teilnehmendenplätze" value={seatsText(a)} testid="activity-review-seats" />
          <DetailRow label="Begleitplätze" value={companionSeats(a) || "Keine Angabe"} testid="activity-review-companion-seats" />
          {p.state === "cancelled" && <DetailRow label="Bisheriger Status" value="Von dir abgesagt" testid="activity-review-previous" />}
          <DetailRow label="Access Pass teilen" value="Nicht ausgewählt · Gesondert möglich" testid="activity-review-pass" />
        </DetailList>
        <CompanionPick a={a} value={companions} onChange={setCompanions} />
        <Link to="/access-pass/teilen" data-testid="activity-review-pass-link" className={`${V3_GHOST} mt-[8px]`}>Access Pass gezielt und gesondert teilen</Link>
        <Outcome p={p} />
        <V3Actions>
          <button type="button" onClick={() => p.act("join", companions ? { companions } : undefined)} disabled={p.busy || !p.can.join} data-testid="activity-join-confirm" className={V3_PRIMARY}>{p.busy ? "Wird übermittelt …" : label}</button>
          <Link to={activityHref(a)} data-testid="activity-flow-back" className={V3_SECONDARY}>Zur Aktivität</Link>
        </V3Actions>
        {full && <p className="mt-[12px] text-[14px] text-v3-muted" data-testid="activity-full-note">Diese Aktivität ist ausgebucht. Frei werdende Plätze werden nicht automatisch vergeben.</p>}
      </>} />
    </div>
  );
}

function StatusView({ a, p, m, title, intro, banner, tone = "sand", icon, rows, actions }) {
  return (
    <div data-m={m} data-testid={`activity-state-${m}`}>
      <V3Title title={title} intro={intro} testid="activity-flow-title" />
      <V3Columns layout="app" aside={<Aside a={a} />} main={<>
        <StatusBanner tone={tone} icon={icon} title={banner} text={text(a.title)} testid="activity-state-banner" />
        <DetailList>{rows.map(([l, v, t]) => <DetailRow key={t} label={l} value={v} testid={t} />)}</DetailList>
        <Outcome p={p} />
        <V3Actions>{actions}</V3Actions>
      </>} />
    </div>
  );
}

const withCompanions = (a, rows, testid) => (ownCompanionText(a) ? [...rows, ["Begleitung", ownCompanionText(a), testid]] : rows);

function Requested({ a, p }) {
  const [ask, setAsk] = useState(false);
  return (<>
    <StatusView a={a} p={p} m="M63" title="Teilnahme angefragt" intro="Deine Anfrage wartet auf die Entscheidung der Gastgebenden." banner="Anfrage offen" icon={CircleHelp}
      rows={withCompanions(a, [["Aktivität", text(a.title), "activity-requested-title"], ["Anfrage", "Ausstehend", "activity-requested-state"], ["Teilnahme", "Noch nicht bestätigt", "activity-requested-part"], ["Nächster Schritt", "Auf Rückmeldung warten oder Anfrage zurückziehen", "activity-requested-next"]], "activity-requested-companions")}
      actions={<>
        <button type="button" onClick={() => setAsk(true)} disabled={p.busy || !p.can.withdraw} data-testid="activity-withdraw" className={V3_PRIMARY}>Anfrage zurückziehen</button>
        <Link to={activityHref(a)} data-testid="activity-flow-back" className={V3_SECONDARY}>Zurück</Link>
        <Recheck p={p} />
      </>} />
    <ConfirmDialog open={ask} onOpenChange={(o) => !p.busy && setAsk(o)} testid="activity-withdraw-dialog" busy={p.busy} title="Anfrage zurückziehen?" confirmLabel="Zurückziehen" cancelLabel="Anfrage behalten"
      onConfirm={async () => { if (await p.act("withdraw")) setAsk(false); }} description={`${text(a.title)} · ${activityWhen(a)}. Deine offene Anfrage wird zurückgezogen.`}>
      {p.error && <ErrorNote testid="activity-withdraw-error">{p.error}</ErrorNote>}
    </ConfirmDialog>
  </>);
}

function Participating({ a, p, state }) {
  return <StatusView a={a} p={p} m="M64" tone="lilacLarge" icon={CalendarCheck} title={state === "confirmed" ? "Teilnahme bestätigt" : "Teilnahme eingetragen"} banner={state === "confirmed" ? "Teilnahme bestätigt" : "Teilnahme eingetragen"}
    intro={state === "confirmed" ? "Deine Teilnahme ist bestätigt. Dein Termin ist in deinen Plänen sichtbar." : "Du bist eingetragen. Dein Termin ist in deinen Plänen sichtbar."}
    rows={withCompanions(a, [["Aktivität", text(a.title), "activity-part-title"], ["Termin", activityWhen(a), "activity-part-when"], ["Treffpunkt", activityPlace(a), "activity-part-place"], ["Teilnahme", state === "confirmed" ? "Bestätigt" : "Eingetragen", "activity-part-state"]], "activity-part-companions")}
    actions={<>
      <Link to="/plaene" data-testid="activity-to-plans" className={V3_PRIMARY}>Zu meinen Plänen</Link>
      <Link to={activityHref(a, "/absagen")} data-testid="activity-leave" className={V3_SECONDARY}>Teilnahme absagen</Link>
    </>} />;
}

// M106 state: on the waitlist; there is no automatic move-up – a seat only comes as an explicit offer (M107).
function Waitlisted({ a, p }) {
  const [ask, setAsk] = useState(false);
  return (<>
    <StatusView a={a} p={p} m="M106" title="Auf der Warteliste" intro="Die Aktivität ist voll. Zugesagt ist damit nichts." banner="Du stehst auf der Warteliste" icon={ListOrdered}
      rows={withCompanions(a, [["Aktivität", text(a.title), "activity-waitlisted-title"], ["Teilnehmendenplätze", seatsText(a), "activity-waitlisted-seats"], ["Teilnahme", "Noch nicht zugesagt", "activity-waitlisted-part"],
        ["Nachrücken", "Nicht automatisch · ein angebotener Platz gilt erst, wenn du ihn annimmst", "activity-waitlisted-next"]], "activity-waitlisted-companions")}
      actions={<>
        <button type="button" onClick={() => setAsk(true)} disabled={p.busy || !p.can.unwait} data-testid="activity-unwait" className={V3_PRIMARY}>Warteliste verlassen</button>
        <Link to={activityHref(a)} data-testid="activity-flow-back" className={V3_SECONDARY}>Zurück</Link>
        <Recheck p={p} />
      </>} />
    <ConfirmDialog open={ask} onOpenChange={(o) => !p.busy && setAsk(o)} testid="activity-unwait-dialog" busy={p.busy} title="Warteliste verlassen?" confirmLabel="Warteliste verlassen" cancelLabel="Auf der Warteliste bleiben"
      onConfirm={async () => { if (await p.act("unwait")) setAsk(false); }} description={`${text(a.title)} · ${activityWhen(a)}. Du wirst von der Warteliste genommen.`}>
      {p.error && <ErrorNote testid="activity-unwait-error">{p.error}</ErrorNote>}
    </ConfirmDialog>
  </>);
}

const OFFER_WITHDRAW = "Du nimmst den angebotenen Platz nicht an und stehst danach nicht mehr auf der Warteliste. Niemand rückt automatisch nach.";

// M107 (v2.10): an offer reserves nothing; accepting is POST …/offer/accept, withdrawing POST …/leave – only the fresh GET decides.
export function Offered({ a, p }) {
  const navigate = useNavigate();
  const [ask, setAsk] = useState(false);
  const accept = async () => { if (await p.accept()) navigate(activityHref(a, "/teilnahme"), { replace: true }); };
  return (<>
    <StatusView a={a} p={p} m="M107" title="Platz aus der Warteliste angeboten" intro={OFFER_TEXT} banner={OFFER_TITLE} icon={Hourglass}
      rows={withCompanions(a, [["Aktivität", text(a.title), "activity-offer-title"], ["Termin", activityWhen(a), "activity-offer-when"], ["Teilnehmendenplätze", seatsText(a), "activity-offer-seats"],
        ["Teilnahme", "Noch nicht bestätigt · kein Platz reserviert", "activity-offer-part"], ["Angebot", "Offen · deine Antwort steht aus", "activity-offer-state"]], "activity-offer-companions")}
      actions={<>
        <button type="button" onClick={accept} disabled={p.busy || !p.can.accept} data-testid="activity-offer-accept" className={V3_PRIMARY}>{p.busy ? "Wird übermittelt …" : "Angebot annehmen"}</button>
        <button type="button" onClick={() => setAsk(true)} disabled={p.busy || !p.can.unoffer} data-testid="activity-offer-withdraw" className={V3_SECONDARY}>Angebot zurückziehen</button>
        <Link to={activityHref(a)} data-testid="activity-flow-back" className={V3_GHOST}>Zurück</Link>
        <Recheck p={p} />
      </>} />
    <ConfirmDialog open={ask} onOpenChange={(o) => !p.busy && setAsk(o)} testid="activity-offer-withdraw-dialog" busy={p.busy} title="Angebot zurückziehen?" confirmLabel="Angebot zurückziehen" cancelLabel="Angebot behalten"
      onConfirm={async () => { if (await p.act("unoffer")) setAsk(false); }} description={`${text(a.title)} · ${activityWhen(a)}. ${OFFER_WITHDRAW}`}>
      {p.error && <ErrorNote testid="activity-offer-withdraw-error">{p.error}</ErrorNote>}
    </ConfirmDialog>
  </>);
}

function Cancelled({ a, p }) {
  return <StatusView a={a} p={p} m="M66" tone="lilacLarge" icon={CalendarX2} title="Aktivität wurde abgesagt" banner="Aktivität abgesagt" intro="Die Aktivität wurde abgesagt. Dein Plan wird entsprechend markiert."
    rows={[["Aktivität", text(a.title), "activity-cancel-title"], ["Ursprünglicher Termin", activityWhen(a), "activity-cancel-when"], ["Grund", text(a.cancel_reason) || "Nicht angegeben", "activity-cancel-reason"], ["Status", "Abgesagt", "activity-cancel-state"]]}
    actions={<>
      <Link to="/aktivitaeten" data-testid="activity-find-other" className={V3_PRIMARY}>Andere Aktivitäten finden</Link>
      <span className="inline-flex min-h-[44px] items-center text-[13px] text-v3-muted" data-testid="activity-contact-host-unavailable">Gastgebende kontaktieren · noch nicht verfügbar</span>
    </>} />;
}

function OtherState({ a, p, state }) {
  return <StatusView a={a} p={p} m={state === "declined" ? "M62" : "M63"} title={state === "declined" ? "Anfrage abgelehnt" : "Status noch unbekannt"} banner={state === "declined" ? "Anfrage abgelehnt" : "Status noch unbekannt"} icon={CircleHelp}
    intro={state === "declined" ? "Die Gastgebenden haben deine Anfrage abgelehnt." : "Das VISIBILIS-System hat deinen Teilnahmestatus nicht eindeutig bestätigt. Prüfe ihn erneut, bevor du etwas anderes tust."}
    rows={[["Aktivität", text(a.title), "activity-other-title"], ["Status", state === "declined" ? "Abgelehnt" : "Unbekannt", "activity-other-state"]]}
    actions={<><Link to={activityHref(a)} data-testid="activity-flow-back" className={V3_SECONDARY}>Zur Aktivität</Link><Recheck p={p} /></>} />;
}

function ParticipationView({ a, onFresh }) {
  const p = useParticipation(a, onFresh);
  if (isFresh(p.state)) return <Review a={a} p={p} />;
  if (p.state === "requested") return <Requested a={a} p={p} />;
  if (p.state === "waitlisted") return <Waitlisted a={a} p={p} />;
  if (p.state === "offered") return <Offered a={a} p={p} />;
  if (p.state === "confirmed" || p.state === "registered") return <Participating a={a} p={p} state={p.state} />;
  if (p.state === "activity_cancelled") return <Cancelled a={a} p={p} />;
  return <OtherState a={a} p={p} state={p.state} />;
}

// M62–M64, M66, M106, M107: one route, the view follows the participation state the Core reports.
export function ActivityParticipation() {
  return <WithActivity>{(a, setFresh) => <ParticipationView a={a} onFresh={setFresh} />}</WithActivity>;
}

// Every cancelling kind is POST …/leave (v2.6 → "storniert"); the review button exists for each of them.
const LEAVE = {
  leave: { status: (s) => (s === "confirmed" ? "Teilnahme bestätigt" : "Teilnahme eingetragen"), title: "Teilnahme absagen?", confirm: "Absage übermitteln", keep: "Teilnahme behalten", effect: "Du wirst ausgetragen; dein Platz wird sofort frei. Niemand rückt automatisch nach." },
  withdraw: { status: () => "Anfrage offen", title: "Anfrage zurückziehen?", confirm: "Anfrage zurückziehen", keep: "Anfrage behalten", effect: "Deine offene Anfrage wird zurückgezogen." },
  unwait: { status: () => "Auf der Warteliste", title: "Warteliste verlassen?", confirm: "Warteliste verlassen", keep: "Auf der Warteliste bleiben", effect: "Du wirst von der Warteliste genommen." },
  unoffer: { status: () => OFFER_TITLE, title: "Angebot zurückziehen?", confirm: "Angebot zurückziehen", keep: "Angebot behalten", effect: OFFER_WITHDRAW },
};
const leaveKind = (can) => ["leave", "withdraw", "unwait", "unoffer"].find((k) => can[k]);

function Leave({ a, onFresh }) {
  const p = useParticipation(a, onFresh);
  const navigate = useNavigate();
  const [ask, setAsk] = useState(false);
  const [first] = useState(() => leaveKind(p.can));
  const kind = leaveKind(p.can) || (p.outcome ? first : undefined);
  if (!kind) return (<><V3Title title="Teilnahme absagen" testid="activity-flow-title" />
    <Unavailable testid="activity-leave-none">Für diese Aktivität gibt es keine Teilnahme, offene Anfrage, keinen Wartelisteneintrag und kein Platzangebot, das du absagen könntest.</Unavailable>
    <Link to={activityHref(a)} className={`${V3_SECONDARY} mt-[18px]`}>Zur Aktivität</Link></>);
  const t = LEAVE[kind];
  const leave = async () => { if (await p.act(kind)) { setAsk(false); navigate(activityHref(a, "/teilnahme"), { replace: true }); } };
  return (
    <div data-m="M65" data-testid="activity-leave-view" data-kind={kind}>
      <V3Title title="Teilnahme absagen" intro="Deine Absage wird erst nach deiner Bestätigung übermittelt." testid="activity-flow-title" />
      <V3Columns layout="app" aside={<Aside a={a} title="Ausgewählter Vorgang" />} main={<>
        <InfoBox title="Deine Bestätigung ist erforderlich" text="Prüfe den angezeigten Vorgang und die konkreten Auswirkungen." testid="activity-leave-note" />
        <DetailList>
          <DetailRow label="Aktivität" value={text(a.title)} testid="activity-leave-title" />
          <DetailRow label="Termin" value={activityWhen(a)} testid="activity-leave-when" />
          <DetailRow label="Nachricht" value="Freiwillig ergänzen · noch nicht verfügbar" testid="activity-leave-message" />
          <DetailRow label="Bisheriger Status" value={t.status(p.state)} testid="activity-leave-state" />
        </DetailList>
        <Outcome p={p} />
        <V3Actions>
          <button type="button" onClick={() => setAsk(true)} disabled={p.busy} data-testid="activity-leave-review" className={V3_PRIMARY}>Absage prüfen</button>
          <Link to={activityHref(a, "/teilnahme")} data-testid="activity-leave-keep" className={V3_SECONDARY}>{t.keep}</Link>
        </V3Actions>
      </>} />
      <ConfirmDialog open={ask} onOpenChange={(o) => !p.busy && setAsk(o)} testid="activity-leave-dialog" busy={p.busy} title={t.title} confirmLabel={t.confirm} cancelLabel={t.keep}
        onConfirm={leave} description={`${text(a.title)} · ${activityWhen(a)}. ${t.effect}`}>
        {p.error && <ErrorNote testid="activity-leave-error">{p.error}</ErrorNote>}
      </ConfirmDialog>
    </div>
  );
}

// M65: cancel only after an explicit confirmation; a message to the host has no documented field (D-44 stays open).
export function ActivityLeave() {
  return <WithActivity>{(a, setFresh) => <Leave a={a} onFresh={setFresh} />}</WithActivity>;
}

const STATES = [["present", "Vorhanden"], ["absent", "Nicht vorhanden"], ["unknown", "Unbekannt"]];

function FeatureCard({ f }) {
  return (
    <li className="rounded-[10px] border border-v3-line bg-white px-[17px] pb-[16px] pt-[12px]" data-testid={`activity-access-card-${f.key}`} data-state={f.state}>
      <p className="text-[17px] font-bold leading-[24px] text-v3-ink">{f.label}</p>
      <div className="mt-[10px] flex flex-col gap-[10px] lg:flex-row lg:items-center lg:justify-between">
        <Pill tone={ACCESS_PILL[f.state][0]}>{ACCESS_PILL[f.state][1]}</Pill>
        <span aria-hidden="true" className="flex gap-[4px] rounded-[8px] bg-v3-lilac p-[3px] text-[12px]">
          {STATES.map(([k, l]) => <span key={k} className={`rounded-[6px] px-[10px] py-[4px] ${k === f.state ? "bg-white font-bold text-v3-ink" : "text-v3-muted"}`}>{l}</span>)}
        </span>
      </div>
      {f.state !== "unknown" && <p className="mt-[8px] text-[12.5px] text-v3-muted">Quelle: {f.source || "keine Angabe"} · Stand: {stand(f)}</p>}
    </li>
  );
}

// M60: every feature with its three possible states; unknown stays open and is never read as present.
export function ActivityAccess() {
  return <WithActivity>{(a) => {
    const meta = accessMeta(a);
    return (
      <div data-m="M60" data-testid="activity-access-view">
        <V3Title title="Zugangsangaben einer Aktivität" intro="Die Zugangsangaben stammen von den Gastgebenden oder der zuständigen Organisation und sind keine Zertifizierung." testid="activity-flow-title" />
        <V3Columns layout="app" aside={<AsideCard icon={CircleHelp} title="Unbekannt bleibt offen" text="Eine ungeklärte Angabe ist keine Bestätigung der Zugänglichkeit." testid="activity-access-aside" />} main={<>
          <ul className="space-y-[11px]">{accessFeatures(a).filter((f) => f.key !== "assistance_dog_info").map((f) => <FeatureCard key={f.key} f={f} />)}</ul>
          <div className="mt-[11px]"><DetailList>
            <DetailRow label="Quelle" value={meta.source || "Keine Angabe"} testid="activity-access-source" />
            <DetailRow label="Stand" value={meta.updatedAt ? new Date(meta.updatedAt).toLocaleDateString("de-CH") : "Nicht angegeben"} testid="activity-access-updated" />
          </DetailList></div>
          <V3Actions>
            <Link to={activityHref(a, "/frage")} data-testid="activity-access-question" className={V3_PRIMARY}>Frage zum Zugang stellen</Link>
            <Link to={activityHref(a)} data-testid="activity-flow-back" className={V3_SECONDARY}>Zurück</Link>
          </V3Actions>
        </>} />
      </div>
    );
  }}</WithActivity>;
}

